// Active-org store. The API is stateless about org selection — the caller names
// the org on every request via the `X-Org-Id` header (there is no server-side
// `/org/switch`). This module is the single source of truth for that id: it is
// read by `request()` (client.ts) to stamp the header, seeded by AuthGate once
// `/me` loads, and updated by `switchOrg` (session.tsx).

const LS_KEY = 'sl_active_org';

let activeOrgId: string | null = readPersisted();

function readPersisted(): string | null {
  try {
    return localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}

/** The org id to send as `X-Org-Id`, or null when none is selected yet. */
export function getActiveOrgId(): string | null {
  return activeOrgId;
}

/** Set (and persist) the active org. No-op when unchanged. */
export function setActiveOrgId(orgId: string | null): void {
  if (orgId === activeOrgId) return;
  activeOrgId = orgId;
  try {
    if (orgId) localStorage.setItem(LS_KEY, orgId);
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* private mode / storage disabled — keep the in-memory value */
  }
}

/**
 * Ensure a valid active org is selected given the user's memberships: keep the
 * persisted one if the user still belongs to it, otherwise fall back to the
 * first membership. Returns the resolved id (empty string when the user has no
 * orgs, e.g. a superadmin). Idempotent — safe to call on every render.
 */
export function ensureActiveOrg(orgs: ReadonlyArray<{ id: string }>): string {
  const stored = activeOrgId;
  const valid = stored && orgs.some((o) => o.id === stored) ? stored : (orgs[0]?.id ?? '');
  if (valid !== stored) setActiveOrgId(valid || null);
  return valid;
}
